import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';

const highlights = [
  { label: "Years Experience", value: 20, suffix: "+" },
  { label: "Professionals Trained", value: 1000, suffix: "+" },
  { label: "Workforce Development", value: "Specialist", isText: true },
  { label: "Corporate Training", value: "Expert", isText: true },
  { label: "Service Excellence", value: "Focus", isText: true }
];

const expertise = [
  "Training & Facilitation", "Train-the-Trainer Programmes", "Customer Service Excellence",
  "Workforce Skills Development", "Staff Coaching & Mentorship", "Learning & Development",
  "Operational Training", "Security Compliance Training", "HRMS Systems Training",
  "Service Culture Development", "Leadership Support Training", "Stakeholder Engagement",
  "Adult Learning Facilitation", "Performance Improvement"
];

const industryExperience = [
  "Aviation", "Education & MOE Programmes", "Security Operations", 
  "Hospitality", "Human Resource Systems", "Dormitory Operations", 
  "Corporate Training", "Workforce Development"
];

const certifications = [
  "ACTA Certified Trainer", "WSQ Train-the-Trainer Certified", 
  "Security Management Diploma", "Service Excellence Diploma", 
  "Leadership Management Diploma", "Certified GHG ISO 14064 Verifier", 
  "Certified Red Teaming Officer", "FSMS Level 4 Certified"
];

const impactSummary = [
  {
    title: "Workforce Development",
    desc: "Delivered structured learning programmes that enhanced employee competencies and workplace performance across multiple industries."
  },
  {
    title: "Service Excellence Training",
    desc: "Facilitated customer service and communication training that strengthened customer experience and employee engagement."
  },
  {
    title: "Train-the-Trainer Facilitation",
    desc: "Equipped supervisors and internal trainers with the skills and methodologies needed to effectively transfer knowledge."
  },
  {
    title: "Operational & Security Training",
    desc: "Supported organisations through practical training programmes focused on workplace compliance and security awareness."
  },
  {
    title: "Learning Culture Enhancement",
    desc: "Promoted continuous learning and professional growth through coaching, mentorship, and skills development initiatives."
  }
];

const AssociateProfile = () => {
  return (
    <section className="py-24 md:py-32 bg-brand-black relative overflow-hidden border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        <div className="grid lg:grid-cols-12 gap-16 items-start">
          
          {/* LEFT SIDE: Portrait Area */}
          <motion.div 
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="lg:col-span-5 relative group lg:order-2"
          >
            <div className="relative z-10 border border-white/10 p-4 bg-white/5 backdrop-blur-sm shadow-2xl overflow-hidden">
              <img 
                src="https://res.cloudinary.com/dfg8nrgvf/image/upload/q_auto/f_auto/v1780918355/86523554bba7bc9ac8e71b0980a97b93_ab9g08.webp" 
                alt="Marcus Krishnan - Associate Trainer" 
                className="w-full aspect-[4/5] object-cover grayscale brightness-90 group-hover:grayscale-0 group-hover:brightness-100 transition-all duration-700"
              />
              <div className="absolute top-0 left-0 w-24 h-24 border-t-2 border-l-2 border-brand-gold -translate-y-full group-hover:translate-y-0 transition-transform duration-500"></div>
            </div>
            <div className="absolute -bottom-6 -left-6 z-20 bg-brand-gold p-6 shadow-xl">
              <p className="text-white text-xs font-black tracking-widest uppercase mb-1">Associate Trainer</p>
              <h3 className="text-white text-2xl font-bold tracking-tight">Marcus Krishnan</h3>
            </div>
          </motion.div>

          {/* RIGHT SIDE: Executive Bio */}
          <div className="lg:col-span-7 pt-10 lg:pt-0 lg:order-1">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="inline-block px-4 py-1 bg-brand-gold/10 border border-brand-gold/20 text-brand-gold text-[10px] font-bold tracking-widest uppercase mb-6">
                Professional Profile
              </div>
              <h2 className="text-4xl md:text-5xl font-bold tracking-tighter text-white mb-8 leading-tight">
                Developing People Through <br /> 
                <span className="text-brand-gold italic">Training Excellence</span>
              </h2>
              <div className="space-y-6 text-gray-400 font-light leading-relaxed text-lg max-w-3xl">
                <p>
                  Marcus Krishnan is an Associate Trainer at White Knight Global with over 20 years of experience delivering impactful training programmes across aviation, education, security, and workforce development.
                </p>
                <p>
                  Throughout his career, Marcus has successfully trained and mentored thousands of learners, helping organisations strengthen service standards, improve operational effectiveness, and develop workforce capabilities.
                </p>
                <p>
                  Marcus brings a practical and engaging approach to learning that supports both individual growth and organisational performance.
                </p>
              </div>

              {/* Highlight Cards */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-12">
                {highlights.map((item, idx) => (
                  <div key={idx} className="p-4 bg-white/5 border border-white/10 text-center">
                    <div className="text-xl font-bold text-white mb-1">
                      {item.isText ? item.value : <span className="text-brand-gold">{item.value}{item.suffix}</span>}
                    </div>
                    <div className="text-[10px] text-gray-500 font-bold uppercase tracking-widest leading-tight">
                      {item.label}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>

        {/* Expertise & Industry Experience */}
        <div className="mt-32 grid lg:grid-cols-2 gap-16">
          <div>
            <h3 className="text-2xl font-bold text-white tracking-tight mb-8 flex items-center gap-4">
              <span className="w-8 h-[1px] bg-brand-gold"></span>
              Areas of Expertise
            </h3>
            <div className="flex flex-wrap gap-2">
              {expertise.map((skill, idx) => (
                <span key={idx} className="px-4 py-2 bg-white/5 border border-white/5 text-gray-400 text-xs font-medium hover:border-brand-gold/50 transition-all">
                  {skill}
                </span>
              ))}
            </div>
          </div>
          <div>
            <h3 className="text-2xl font-bold text-white tracking-tight mb-8 flex items-center gap-4">
              <span className="w-8 h-[1px] bg-brand-gold"></span>
              Industry Experience
            </h3>
            <div className="flex flex-wrap gap-2">
              {industryExperience.map((industry, idx) => (
                <span key={idx} className="px-4 py-2 bg-brand-gold/5 border border-brand-gold/10 text-brand-gold text-xs font-medium">
                  {industry}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Impact Summary Grid */}
        <div className="mt-24 grid md:grid-cols-3 lg:grid-cols-5 gap-6">
          {impactSummary.map((item, idx) => (
            <motion.div 
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1 }}
              className="p-8 bg-brand-gray/5 border border-white/5 hover:bg-brand-gray/10 transition-all group"
            >
              <h4 className="text-brand-gold font-bold text-sm uppercase tracking-widest mb-4">{item.title}</h4>
              <p className="text-gray-400 text-xs leading-relaxed font-light">{item.desc}</p>
            </motion.div>
          ))}
        </div>

        {/* Certifications & Quote */}
        <div className="mt-24 grid lg:grid-cols-2 gap-12 items-center">
          <div className="grid sm:grid-cols-2 gap-4">
            {certifications.map((cert, idx) => (
              <div key={idx} className="flex items-center gap-4 p-4 bg-white/5 border border-white/5">
                <SafeIcon name="Check" className="text-brand-gold text-sm shrink-0" />
                <span className="text-xs text-gray-300 font-medium">{cert}</span>
              </div>
            ))}
          </div>
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="p-10 bg-white border border-brand-gold/20 text-brand-black relative"
          >
            <SafeIcon name="Quote" className="text-4xl text-brand-gold/20 mb-6" />
            <p className="text-xl font-light italic leading-relaxed mb-8">
              "Marcus has trained and mentored thousands of professionals across aviation, education, security, hospitality, and corporate environments. His passion for workforce development enables organisations to build capable teams."
            </p>
            <div className="flex items-center gap-4">
              <div className="w-10 h-[1px] bg-brand-gold"></div>
              <span className="text-xs font-bold uppercase tracking-widest">Professional Impact Statement</span>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default AssociateProfile;